import { useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Table } from 'shared/ui/Table';
import type { CampaignTypeKey } from 'entities/campaign/@x';
import { Roles, useClientDataStore } from 'shared/lib/store';

import type { BidRangeBidParams, BidRangeBidTierGroups, BidRangeProps } from './types';
import { checkIsLessThanMinimumBid, getColumns, getMinBidDollars, getTableData } from './BidRangeUtils';

interface CheckOneFieldLessThanMinimumParams {
  bidValue?: string;
  bidTiersValues?: { bid: string; id: number }[];
  bidParams?: BidRangeBidParams | null;
  bidTierGroups?: BidRangeBidTierGroups[] | null;
  campaignType: CampaignTypeKey;
}

function checkOneFieldLessThanMinimum({
  bidValue,
  bidTiersValues,
  bidParams,
  bidTierGroups,
  campaignType,
}: CheckOneFieldLessThanMinimumParams) {
  const bidTiers = bidTierGroups?.[0]?.bidTiers ?? [];

  if (bidParams) {
    return !!(bidValue && checkIsLessThanMinimumBid(getMinBidDollars({ campaignType, ...bidParams }), bidValue));
  }

  return bidTiers.reduce((acc, bidTier, i) => {
    if (
      bidTiersValues &&
      bidTiersValues[i] &&
      bidTiersValues[i].bid &&
      checkIsLessThanMinimumBid(getMinBidDollars({ campaignType, ...bidTier }), bidTiersValues[i].bid)
    ) {
      return true;
    }

    return acc;
  }, false);
}

export function BidRange({
  isFocused,
  isWideScreen,
  isDisabled,
  className,
  campaignType,
  bidParams,
  bidTierGroups,
  dataTestId = '',
  isLessThanMinimumTooltip,
  setRate,
}: BidRangeProps) {
  const [bidValue, bidTiersValues] = useWatch({ name: ['bid', 'bidTiers'] });
  const isOneOfFieldsLessThanMinimum = checkOneFieldLessThanMinimum({
    bidValue,
    bidTiersValues,
    bidParams,
    bidTierGroups,
    campaignType,
  });

  const { getRole } = useClientDataStore();
  const role = getRole();

  const isContractor = role === Roles.Contractor;

  const isLessThanMinimumTooltipShow = isLessThanMinimumTooltip && isContractor;
  const isOneOfFieldsLessThanMinimumShow = isOneOfFieldsLessThanMinimum && isContractor;

  const tableData = getTableData({
    campaignType,
    bidParams,
    bidTierGroups,
    isLessThanMinimumTooltip: isLessThanMinimumTooltipShow,
    isOneOfFieldsLessThanMinimum: isOneOfFieldsLessThanMinimumShow,
    bidValue,
    bidTiersValues,
  });
  const columns = getColumns({
    isWideScreen,
    isDisabled,
    isFocused,
    bidTierColumnTitle: bidTierGroups?.[0]?.label,
    isOneOfFieldsLessThanMinimum: isOneOfFieldsLessThanMinimumShow,
    setRate,
  });

  const methods = useFormContext();
  const errors = methods.formState.errors;
  const getErrorMessage = useCallback(() => {
    if (errors.bid) {
      return errors.bid.message?.toString();
    }
    if (Array.isArray(errors.bidTiers)) {
      const bidTier = errors.bidTiers.filter((x) => x)[0];

      return bidTier?.bid?.message?.toString().replace(/bidTiers\.\d+\.bid/, 'bid');
    }

    return '';
  }, [errors.bid, errors.bidTiers]);

  return (
    <div
      className={className}
      data-testid={dataTestId}
    >
      <Table
        className="!mt-0"
        isDisabled={isDisabled}
        data={tableData}
        variant="table-sm"
        columns={columns}
        rowKey={({ index }) => index}
      />
      <div className="mt-1 px-4 text-xs font-medium text-red-500">{getErrorMessage()}</div>
    </div>
  );
}
